import { createContext, useMemo } from 'react';
import { useSearchOrders, useSearchShipments } from 'utils/hooks';

import {
  IOrderSearch,
  ShipmentResDataDto,
} from '@project44-models/shipment-list';

const defaultData = {
  results: [],
  paginationInfo: {
    total: 0,
    pageSize: 1,
    pageNumber: 1,
  },
};

const defaultList = {
  doCountOrders: () => false,
  doCountShipments: () => false,
  doSearchOrders: () => false,
  doSearchShipments: () => false,
  isErrorOrders: false,
  isErrorShipments: false,
  isLoadingOrders: true,
  isLoadingShipments: true,
  ordersData: defaultData,
  shipmentsData: ShipmentResDataDto.fromJSON(defaultData),
  totalCountOrders: undefined,
  totalCountShipments: undefined,
};

interface IListContext {
  doCountOrders: () => void;
  doCountShipments: () => void;
  doSearchOrders: (page?: { page: number; size: number }) => void;
  doSearchShipments: (
    page?: { page: number; size: number },
    additionalAttributes?: string[],
  ) => void;
  isErrorOrders: boolean;
  isErrorShipments: boolean;
  isLoadingOrders: boolean;
  isLoadingShipments: boolean;
  ordersData: IOrderSearch;
  shipmentsData: ShipmentResDataDto;
  totalCountOrders?: number;
  totalCountShipments?: number;
}

const ListContext = createContext<IListContext>(defaultList);

interface IListContextProvider {
  children: React.ReactNode;
}

function ListContextProvider({ children }: IListContextProvider) {
  const shipments = useSearchShipments();
  const orders = useSearchOrders();

  const value = useMemo(() => {
    const {
      data: shipmentsData,
      doCount: doCountShipments,
      doSearch: doSearchShipments,
      isError: isErrorShipments,
      isLoading: isLoadingShipments,
      totalCount: totalCountShipments,
    } = shipments;
    const {
      data: ordersData,
      doCount: doCountOrders,
      doSearch: doSearchOrders,
      isError: isErrorOrders,
      isLoading: isLoadingOrders,
      totalCount: totalCountOrders,
    } = orders;
    return {
      doCountOrders,
      doCountShipments,
      doSearchOrders,
      doSearchShipments,
      isErrorOrders,
      isErrorShipments,
      isLoadingOrders,
      isLoadingShipments,
      ordersData,
      shipmentsData,
      totalCountOrders,
      totalCountShipments,
    };
  }, [shipments, orders]);

  return <ListContext.Provider value={value}>{children}</ListContext.Provider>;
}

export { ListContext, ListContextProvider };
